import { supabase } from '../../lib/supabase';
import type { RentalContract, RentalContractFormData } from '../../types/rental-contract';

export const supabaseRentalContractService = {
  // Get all rental contracts
  getAll: async (): Promise<RentalContract[]> => {
    console.log('Fetching all rental contracts...');
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return [];
    }
    
    try {
      // Use direct foreign key reference query
      const { data, error } = await supabase
        .from('rental_contract')
        .select(`
          rental_trans_id,
          rental_lease,
          rental_amount,
          rental_start,
          sl:subleases!rental_lease(
            lease_holder,
            lh:leaseholders!lease_holder(
              holder_name
            )
          )
        `)
        .order('rental_trans_id', { ascending: false });

      if (error) {
        console.error('Error fetching rental contracts:', error);
        throw error;
      }

      if (!data) {
        console.log('No rental contracts found');
        return [];
      }

      // Map the results to include the leaseholder name
      const contractsWithLeaseholder = data.map((contract: any) => {
        const holderName = contract.sl?.lh?.holder_name || 'N/A';
        
        return {
          rental_trans_id: contract.rental_trans_id,
          rental_lease: contract.rental_lease,
          rental_amount: contract.rental_amount,
          rental_start: contract.rental_start,
          sublease_info: {
            lease_id: contract.rental_lease,
            holder_name: holderName
          }
        };
      });

      return contractsWithLeaseholder;
    } catch (error) {
      console.error('Error in getAll rental contracts:', error);
      return [];
    }
  },

  // Get a single rental contract by ID
  getById: async (id: number): Promise<RentalContract | null> => {
    console.log(`Fetching rental contract with ID: ${id}`);
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return null;
    }
    
    try {
      // Get the basic rental contract information
      const { data, error } = await supabase
        .from('rental_contract')
        .select('rental_trans_id, rental_lease, rental_amount, rental_start')
        .eq('rental_trans_id', id)
        .single();

      if (error) {
        console.error(`Error fetching rental contract with ID ${id}:`, error);
        return null;
      }

      if (!data) {
        console.log(`No rental contract found with ID ${id}`);
        return null;
      }

      // If there's no associated lease, return just the basic info
      if (!data.rental_lease) {
        return {
          rental_trans_id: data.rental_trans_id,
          rental_lease: null,
          rental_amount: data.rental_amount,
          rental_start: data.rental_start
        };
      }

      // Get the associated sublease information
      try {
        const { data: subleaseData, error: subleaseError } = await supabase
          .from('subleases')
          .select(`
            lease_id,
            lease_lots:lease_lots (
              land_lots:land_lots (
                lot_number,
                communities:communities (
                  comm_name
                )
              )
            ),
            leaseholders:leaseholders (
              holder_name
            )
          `)
          .eq('lease_id', data.rental_lease)
          .single();

        if (subleaseError || !subleaseData) {
          return {
            rental_trans_id: data.rental_trans_id,
            rental_lease: data.rental_lease,
            rental_amount: data.rental_amount,
            rental_start: data.rental_start
          };
        }

        // Extract information from the sublease using type assertions
        const landLot = subleaseData.lease_lots?.[0]?.land_lots;
        
        // Use type assertions to handle the complex nested structure
        return {
          rental_trans_id: data.rental_trans_id,
          rental_lease: data.rental_lease,
          rental_amount: data.rental_amount,
          rental_start: data.rental_start,
          sublease_info: {
            lease_id: subleaseData.lease_id,
            lot_number: landLot ? (landLot as any).lot_number : undefined,
            community_name: landLot ? (landLot as any).communities?.[0]?.comm_name : undefined,
            holder_name: subleaseData.leaseholders?.[0]?.holder_name
          }
        };
      } catch (subleaseErr) {
        console.error(`Error fetching sublease info for lease ${data.rental_lease}:`, subleaseErr);
        return {
          rental_trans_id: data.rental_trans_id,
          rental_lease: data.rental_lease,
          rental_amount: data.rental_amount,
          rental_start: data.rental_start
        };
      }
    } catch (error) {
      console.error(`Error in getById for rental contract ${id}:`, error);
      return null;
    }
  },

  // Create a new rental contract
  create: async (rentalContract: RentalContractFormData): Promise<RentalContract | null> => {
    console.log('Creating new rental contract:', rentalContract);
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return null;
    }
    
    try {
      const { data, error } = await supabase
        .from('rental_contract')
        .insert([{
          rental_lease: rentalContract.rental_lease,
          rental_amount: rentalContract.rental_amount,
          rental_start: rentalContract.rental_start
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating rental contract:', error);
        throw error;
      }

      if (!data) {
        console.log('No data returned after creating rental contract');
        return null;
      }

      return {
        rental_trans_id: data.rental_trans_id,
        rental_lease: data.rental_lease,
        rental_amount: data.rental_amount,
        rental_start: data.rental_start
      };
    } catch (error) {
      console.error('Error in create rental contract:', error);
      throw error;
    }
  },

  // Update an existing rental contract
  update: async (id: number, rentalContract: RentalContractFormData): Promise<RentalContract | null> => {
    console.log(`Updating rental contract with ID ${id}:`, rentalContract);
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return null;
    }
    
    try {
      const { data, error } = await supabase
        .from('rental_contract')
        .update({
          rental_lease: rentalContract.rental_lease,
          rental_amount: rentalContract.rental_amount,
          rental_start: rentalContract.rental_start
        })
        .eq('rental_trans_id', id)
        .select()
        .single();

      if (error) {
        console.error(`Error updating rental contract with ID ${id}:`, error);
        throw error;
      }

      if (!data) {
        console.log(`No data returned after updating rental contract with ID ${id}`);
        return null;
      }

      return {
        rental_trans_id: data.rental_trans_id,
        rental_lease: data.rental_lease,
        rental_amount: data.rental_amount,
        rental_start: data.rental_start
      };
    } catch (error) {
      console.error(`Error in update for rental contract ${id}:`, error);
      throw error;
    }
  },

  // Delete a rental contract
  delete: async (id: number): Promise<boolean> => {
    console.log(`Deleting rental contract with ID ${id}`);
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return false;
    }
    
    try {
      const { error } = await supabase
        .from('rental_contract')
        .delete()
        .eq('rental_trans_id', id);

      if (error) {
        console.error(`Error deleting rental contract with ID ${id}:`, error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error(`Error in delete for rental contract ${id}:`, error);
      throw error;
    }
  }
};
